import { DOCUMENT } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { filter } from 'rxjs';
import { AppUpdateService } from './core/services/app-update.service';
import { LanguageService } from './core/services/language.service';
import { PageSeoData, SeoService } from './core/services/seo.service';

/** Descend jusqu'à la route activée la plus profonde — c'est elle qui porte `data.seo` (voir public-site.routes.ts). */
function deepestSnapshot(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  return snapshot.firstChild ? deepestSnapshot(snapshot.firstChild) : snapshot;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);
  private readonly appUpdate = inject(AppUpdateService);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);

  /** Dernière langue effectivement appliquée à ngx-translate — voir l'effect ci-dessous. */
  private appliedLang: string | null = null;

  constructor() {
    this.appUpdate.init();

    // Si une langue n'a pas (ou plus) de bundle de traduction (public/i18n/*.json), ngx-translate
    // reste en français : currentLang() reste l'état affiché par le sélecteur, translate.use() ne
    // suit que les langues avec bundle (voir LanguageService.hasTranslation).
    //
    // translate.use() ne doit être appelé QUE quand la langue change réellement : il ré-émet
    // onLangChange même en le rappelant avec la même langue, ce que les composants abonnés à
    // onLangChange (ex. FaqSection, ProgramComponentsComponent, About — voir leur constructeur)
    // retraduisent en réécrivant un signal, ce qui redéclenche cet effect en boucle infinie tant
    // que l'appel n'est pas gardé (observé en SSR : `ng serve`/`ng build` restaient bloqués sur
    // toute page rendue côté serveur, CPU à 100 % sans jamais répondre).
    effect(() => {
      const lang = this.language.currentLang();
      const active = this.language.hasTranslation(lang) ? lang : 'fr';
      this.document.documentElement.lang = active;
      if (active === this.appliedLang) return;
      this.appliedLang = active;
      this.translate.use(active);
    });

    /**
     * SEO applicable uniquement aux pages publiques déclarant `data.seo` (voir
     * public-site.routes.ts) : les espaces authentifiés (admin/jeune/partenaire) et les pages de
     * détail (contenu chargé en HTTP, qui posent leur propre SEO une fois les données réelles
     * disponibles) n'en portent volontairement pas.
     */
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const data = deepestSnapshot(this.router.routerState.snapshot.root).data as { seo?: PageSeoData };
      if (data.seo) this.seo.set(data.seo);
    });
  }
}
